/**
 * Garde la session ouverte : le jeton d'accès (JWT, 1 h) est renouvelé avec le jeton de
 * rafraîchissement quelques minutes avant d'expirer, et au retour sur l'onglet s'il a expiré
 * entre-temps. Sans ça, les éditeurs repris du site (qui effacent la session au premier 401)
 * déconnecteraient le gestionnaire au bout d'une heure.
 */
import { useEffect } from 'react'
import { refreshAccessToken } from '@web/features/auth/utils/refreshAccessToken'
import { AUTH_TOKENS_CHANGED_EVENT, getAccessToken, getRefreshToken } from '@web/features/auth/utils/tokenStorage'

const MARGIN_MS = 3 * 60 * 1000

/** Date d'expiration (ms) lue dans le JWT, ou null s'il est illisible. */
export function tokenExpiry(token: string | null): number | null {
  if (!token) return null
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

function needsRefresh(): boolean {
  const exp = tokenExpiry(getAccessToken())
  return Boolean(getRefreshToken()) && (exp === null || exp - Date.now() < MARGIN_MS)
}

/** Au démarrage : si le jeton d'accès est expiré (ou presque), on le renouvelle avant le premier appel. */
export async function ensureFreshSession(): Promise<void> {
  if (needsRefresh()) await refreshAccessToken()
}

export function useKeepSession() {
  useEffect(() => {
    let timer: number | undefined
    const schedule = () => {
      window.clearTimeout(timer)
      if (!getRefreshToken()) return
      if (needsRefresh()) {
        void refreshAccessToken()
        return
      }
      const exp = tokenExpiry(getAccessToken())
      if (exp) timer = window.setTimeout(() => void refreshAccessToken(), Math.max(5_000, exp - Date.now() - MARGIN_MS))
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule()
    }
    schedule()
    window.addEventListener(AUTH_TOKENS_CHANGED_EVENT, schedule)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener(AUTH_TOKENS_CHANGED_EVENT, schedule)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}
